import { Client } from '@/types/client';
import { SellerShippingAddress } from '@/types/item';

export type Timeline = {
  kind: TimelineKind; // Add at Service
  analyticsTracking?: TimelineAnalyticsTracking;
  header?: TimelineHeader;
  isDirectShipping?: boolean;
  printInvoiceUrl?: string;
  status: TimelineStatus;
  steps: TimelineSteps;
  timelineType?: TimelineType;
  getPaidNotice?: GetPaidNotice;
  actions?: Action[];
  sellerShippingAddress?: SellerShippingAddress | null;
  seller?: Client | null;
};

export type Action = {
  name: 'callback';
  type: string;
  message: string;
  subtitle: string;
  messageTracking: string;
  url: string;
  title: string;
};

export type GetPaidNotice = {
  header: string;
  message: string;
  paymentSetupStatus: string;
  title: string;
  url: string;
};

export type TimelineKind = 'Buyer' | 'Seller';

export type TimelineType = 'directShipping' | 'standardShipping' | 'vipShipping';

export interface TimelineAnalyticsTracking {
  buyerStatus?: string;
  sellerStatus?: string;
}

export interface TimelineHeader {
  date?: string;
  message: string;
  status: 'inProgress' | 'complete';
  step: string;
  note?: string;
}

export type TimelineStatus = TimelineBuyerDataStatus & TimelineSellerDataStatus;

export type TimelineSteps = Record<string, TimelineStep | string[]>;

export type DeliveryType = 'home' | 'pickup';

export type HyperLink = {
  message: string;
  additionalInfo?: string;
  note?: string;
};

export enum ShipmentLabelTypeEnum {
  shippingCode = 'shipping_code',
  printableLabel = 'printable_label',
}

export enum ShippingPickupTypeEnum {
  homeCollection = 'home_collection',
  dropOff = 'drop_off',
}

export type ShipmentDetails = {
  chosenCarrier?: {
    carrierName: string;
    iconUrl: string;
    carrierNote?: string;
  };
  labelType?: ShipmentLabelTypeEnum;
  labelUrl?: string;
  shipByDate?: string;
  shippingLabelUrl?: string;
  trackingNumber?: string;
  shippingOption?: ShippingPickupTypeEnum;
  shippingInstruction?: string;
  homeCollectionLink?: string;
  homeCollectionPhoneNumber?: string;
  dropOffLocationLink?: string;
  dropOffMapAvailable?: boolean;
};

export type TimelineStep = {
  identifier: string; // Add at view
  deliveryType?: DeliveryType;
  additionalInfo?: string;
  displayDate?: string;
  estimatedDisplayDate?: string;
  nextStepStatus: TimelineStepStatus;
  status: TimelineStepStatus;
  title: string;
  message?: string;
  hyperlink?: HyperLink;
  messageKey?: string;
  tracking?: TimelineTracking;
  actions?: TimelineActions;
  pickUpLocationDetails?: TimelinePickupLocationDetails;
  relistDetails?: TimelineRelistDetails;
  trackingStep?: string;
  proofOfDelivery?: ProofOfDelivery;
  payoutDetails?: PayoutDetails;
  cancellationPolicy?: TimelineCommonAction;
  note?: string;
  shipmentDetails?: ShipmentDetails;
};

export type ProofOfDelivery = {
  message: string;
  title: string;
  url: string;
};

export type TimelineStepStatus = 'none' | 'ongoing' | 'pending' | 'done' | 'cancelled' | 'upcoming';

export interface TimelineTracking {
  carrierLabel?: string;
  carrierName?: string;
  label?: string;
  logoUrl?: string;
  number: string;
  url?: string;
}

export interface TimelinePickupLocationDetails {
  address?: string;
  carrier?: TimelinePickupCarrier;
  city?: string;
  distance?: string;
  hasDisabledAccess?: boolean;
  isLegacyPickup?: boolean;
  legacyIdPoint?: number;
  legacyMnemonicSocolissimo?: string;
  legacyTypePoint?: string;
  location?: unknown;
  name?: string;
  openingTimes?: TimelinePickupOpeningTimes;
  photos?: unknown;
  postcode?: string;
  reference?: string;
  serviceLevel?: string;
}

export interface TimelinePickupOpeningTimes {
  friday?: string;
  monday?: string;
  saturday?: string;
  sunday?: string;
  thursday?: string;
  tuesday?: string;
  wednesday?: string;
}

export interface TimelinePickupCarrier {
  iconUrl?: string;
  name?: string;
}

export type TrackingData = {
  action: string;
  category: string;
  label: string;
  productId: string;
  property: string;
};

export interface TimelineEditShippingDetailsAction {
  title: string;
  url: string;
}

export type TimelineActions = Record<string, TimelineAction | string[]>;

export type TimelineActionStyle = 'primary' | 'outline' | 'underline' | 'none';

export enum ActionIdentifier {
  cancel = 'cancel',
  confirmDelivery = 'confirmDelivery',
  contact = 'contact',
  download = 'download',
  qualityControlReport = 'qualityControlReport',
  relist = 'relist',
  report = 'report',
  returnForm = 'returnForm',
  withdrawReturn = 'withdrawReturn',
  callback = 'callback',
  getPaid = 'getPaid',
  shippingLabel = 'shippingLabel',
  editShippingDetails = 'editShippingDetails',
  uploadInvoice = 'uploadInvoice',
  updateAddress = 'updateAddress',
  accept = 'accept',
}

export type TimelineAction =
  | (TimelineCancelAction & {
      identifier: ActionIdentifier.cancel;
    })
  | (TimelineConfirmDeliveryAction & {
      identifier: ActionIdentifier.confirmDelivery;
    })
  | (TimelineContactAction & {
      identifier: ActionIdentifier.contact;
    })
  | (TimelineDownloadAction & {
      identifier: ActionIdentifier.download;
    })
  | (TimelineQualityControlReportAction & {
      identifier: ActionIdentifier.qualityControlReport;
    })
  | (TimelineRelistAction & {
      identifier: ActionIdentifier.relist;
    })
  | (TimelineReportAction & {
      identifier: ActionIdentifier.report;
    })
  | (TimelineReturnFormAction & {
      identifier: ActionIdentifier.returnForm;
    })
  | (TimelineCommonAction & {
      identifier: ActionIdentifier.withdrawReturn;
    })
  | (TimelineCallbackAction & {
      identifier: ActionIdentifier.callback;
    })
  | (TimelineCallbackAction & {
      identifier: ActionIdentifier.getPaid;
    })
  | (TimelineShippingLabelAction & {
      identifier: ActionIdentifier.shippingLabel;
    })
  | (TimelineEditShippingDetailsAction & {
      identifier: ActionIdentifier.editShippingDetails;
    })
  | (TimelineCommonAction & {
      identifier: ActionIdentifier.uploadInvoice;
    })
  | (TimelineCommonAction & {
      identifier: ActionIdentifier.updateAddress;
    })
  | (TimelineCommonAction & {
      identifier: ActionIdentifier.accept;
    });

export interface TimelineCancelAction {
  title: string;
  url: string;
}

export interface TimelineShippingLabelAction {
  title: string;
  url: string;
  tracking: TrackingData;
  type?: string;
}

export interface TimelineConfirmDeliveryAction {
  clientId: string;
  title: string;
  tmsShipmentId: string;
  deliveryType: DeliveryType;
}

export interface TimelineContactAction {
  customerId?: string;
  title?: string;
  type?: 'buyer' | 'seller';
  tracking?: TrackingData;
}

export interface TimelineDownloadAction {
  type?: 'none' | 'returnLabel' | 'shippingLabel';
  title: string;
  url: string;
}

export interface TimelineQualityControlReportAction {
  label?: string;
  type: 'none' | 'button' | 'link';
  title: string;
  url: string;
  tracking?: TrackingData;
}

export interface TimelineRelistAction {
  title: string;
  url: string;
  style?: TimelineActionStyle;
}

export interface TimelineReportAction {
  title: string;
  url: string;
  type: 'report' | 'viewIssue';
}

export interface TimelineReturnFormAction {
  title: string;
  url: string;
  returnReasons: Record<string, string>;
  tracking?: TrackingData;
}

export interface TimelineCallbackAction {
  title: string;
  subtitle?: string;
  url?: string;
  message?: string;
  messageTracking?: string;
  tracking?: TrackingData;
}

export interface TimelineRelistDetails {
  fee?: string;
  publicPrice?: string;
  sellerPrice?: string;
}

export interface TimelineCommonAction {
  url: string;
  title: string;
  tracking: TrackingData;
  style?: TimelineActionStyle;
}

export interface PayoutDetails {
  account: string;
  currency: string;
  formattedAmount: string;
  paymentBy: string;
  failedReason?: string;
  message: string;
  paymentStatus: string;
}

export type TimelineBuyerDataStatus =
  | 'none'
  | 'orderComplete'
  | 'shipmentInProgress'
  | 'shipmentComplete'
  | 'deliveryToHubInProgress'
  | 'deliveryToHubComplete'
  | 'itemAssessmentInProgress'
  | 'itemAssessmentComplete'
  | 'shipmentFromHubInProgress'
  | 'shipmentFromHubComplete'
  | 'deliveryInProgress'
  | 'deliveryComplete'
  | 'conformityInProgress'
  | 'conformityComplete'
  | 'refundInProgress'
  | 'refundComplete';

export type TimelineSellerDataStatus =
  | 'none'
  | 'saleComplete'
  | 'shipmentInProgress'
  | 'shipmentComplete'
  | 'deliveryToHubInProgress'
  | 'deliveryToHubComplete'
  | 'itemAssessmentInProgress'
  | 'itemAssessmentComplete'
  | 'deliveryInProgress'
  | 'deliveryComplete'
  | 'conformityInProgress'
  | 'conformityComplete'
  | 'paymentInProgress'
  | 'paymentComplete';
